import React from "react";
import { motion } from "framer-motion";
import { FaFacebook, FaInstagram, FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaHeart, FaGraduationCap } from "react-icons/fa";
import logo from "../assets/bubuschool.png";

const Footer = () => {
  return (
    <footer className="bg-gradient-to-br from-gray-900 via-slate-800 to-gray-900 text-white py-16 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-secondary-500/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
      <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-accent-500/5 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">

          {/* Brand Section */}
          <motion.div
            className="lg:col-span-2 space-y-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center space-x-4">
              <motion.img
                src={logo}
                alt="Smart Educare Centre Logo"
                className="w-16 h-16"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ duration: 0.3 }}
              />
              <div>
                <h2 className="text-2xl font-bold font-platypi gradient-text bg-gradient-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent">
                  Smart Educare Centre
                </h2>
                <p className="text-gray-400 font-sedan italic">
                  "We Create a Smart Generation"
                </p>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed max-w-md">
              Nurturing young minds with innovative educational approaches, creating a foundation
              for lifelong learning and success in a caring, supportive environment.
            </p>

            {/* Social Links */}
            <div className="flex space-x-4">
              <motion.a
                href="#"
                className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center text-white hover:shadow-glow transition-all duration-300"
                whileHover={{ scale: 1.1, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaFacebook size={20} />
              </motion.a>
              <motion.a
                href="#"
                className="w-12 h-12 bg-gradient-secondary rounded-full flex items-center justify-center text-white hover:shadow-glow transition-all duration-300"
                whileHover={{ scale: 1.1, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaInstagram size={20} />
              </motion.a>
            </div>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl font-bold font-platypi text-white mb-4 flex items-center">
              <FaMapMarkerAlt className="mr-2 text-primary-400" />
              Location
            </h3>

            <div className="space-y-4">
              <div className="glass-effect rounded-xl p-4">
                <h4 className="font-semibold text-primary-300 mb-2">Head Office</h4>
                <p className="text-sm text-gray-300 leading-relaxed">
                  Shop No. 7, Parshuram Plaza,<br />
                 opposite classmate cafe, Katrap, Badlapur,<br />
                  Maharashtra, India
                </p>
              </div>
            </div>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl font-bold font-platypi text-white mb-4">
              📞 Contact Us
            </h3>

            <div className="space-y-4">
              <motion.a
                href="tel:+919834387679"
                className="flex items-center space-x-3 glass-effect rounded-xl p-4 hover:bg-white/10 transition-all duration-300 group"
                whileHover={{ x: 5 }}
              >
                <div className="group-hover:animate-bounce">
                  <p className="font-semibold">📞 Call Us</p>
                  <p className="text-sm text-gray-300">+91 9834387679</p>
                </div>
              </motion.a>

              <motion.a
                href="mailto:centresmarteducare@gmail.com"
                className="flex items-center space-x-3 glass-effect rounded-xl p-4 hover:bg-white/10 transition-all duration-300 group"
                whileHover={{ x: 5 }}
              >
                <FaEnvelope className="text-accent-400 group-hover:animate-bounce" />
                <div>
                  <p className="font-semibold">Email Us</p>
                  <p className="text-sm text-gray-300">centresmarteducare@gmail.com</p>
                </div>
              </motion.a>
            </div>
          </motion.div>
        </div>

        {/* Bottom Section */}
        <motion.div
          className="border-t border-white/10 mt-12 pt-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-2 text-gray-400">
              <FaGraduationCap className="text-primary-400" />
              <span className="text-sm">
                © 2025 Smart Educare Centre. All rights reserved.
              </span>
            </div>

            <div className="flex items-center space-x-2 text-gray-400">
              <span className="text-sm">Made with</span>
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1, repeat: Infinity, repeatDelay: 2 }}
              >
                <FaHeart className="text-red-400" />
              </motion.div>
              <span className="text-sm">for education</span>
            </div>
          </div>

          {/* Call to Action */}
          <motion.div
            className="text-center mt-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            viewport={{ once: true }}
          >
            <p className="text-gray-300 mb-4">Ready to give your child the best start in education?</p>
            <motion.a
              href="https://wa.me/919834387679?text=Hi! I'm ready to give my child the best start in education at Smart Educare Center. Please provide complete information about enrollment process, available programs, fees, admission requirements, and next steps. I'm excited to join your educational family! Thank you!"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-primary text-white px-8 py-3 rounded-xl font-semibold shadow-medium hover:shadow-strong transition-all duration-300 inline-flex items-center space-x-2"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787"/>
              </svg>
              <span>Enroll Today</span>
            </motion.a>
          </motion.div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
